import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Icon } from '~/components';
import { colors } from '~/styles';
import Chart from './Chart';
import Markdown from './Markdown';
import { resolveRenderer } from './renderers';
import ResponseActions from './ResponseActions';
import ToolStatusBar from './ToolStatusBar';

const sweep = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
`;

function barSweep(onBand) {
  const base = onBand ? colors.grey20 : colors.grey10;
  const highlight = onBand ? colors.grey40 : colors.grey25;
  return css`
    background: linear-gradient(
      90deg,
      ${base} 0%,
      ${base} 40%,
      ${highlight} 50%,
      ${base} 60%,
      ${base} 100%
    );
    background-size: 200% 100%;
    animation: ${sweep} 2.2s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      background: ${base};
    }
  `;
}

const S = {
  Turn: styled.div`
    position: relative;
    display: flex;
    align-items: flex-start;
    margin-top: 0.5rem;

    ${(props) =>
      props.$last &&
      css`
        min-height: 70vh;
      `}
  `,

  Message: styled.div`
    align-self: flex-start;
    width: 100%;
    position: relative;

    &:hover .response-actions {
      opacity: 1;
    }
    color: ${colors.black};
    overflow: hidden;
    line-height: 1.5;

    h1,
    h2,
    h3 {
      font-weight: 600;
      color: ${colors.black};
      margin: 1rem 0 0.5rem;
    }

    h1 {
      font-size: 1.125rem;
    }

    h2 {
      font-size: 1rem;
    }

    h3 {
      font-size: 0.875rem;
    }

    p {
      margin-bottom: 0.5rem;
    }

    blockquote {
      border-left: 0.1875rem solid ${colors.grey25};
      padding-left: 0.75rem;
      margin: 0.5rem 0;
      color: ${colors.grey75};
    }

    ol {
      margin-bottom: 0.5rem;
      padding-left: 1rem;
      list-style-position: inside;
    }

    ul {
      margin-bottom: 0.5rem;
      list-style-position: inside;
      list-style-type: disc;
      padding-left: 1rem;

      ul {
        list-style-type: circle;

        ul {
          list-style-type: square;
        }
      }
    }

    li > p {
      display: inline;
      margin-bottom: 0;
    }
  `,

  ErrorBlock: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: ${({ $variant }) => ($variant === 'warning' ? colors.warning10 : colors.danger5)};
    border: 1px solid ${({ $variant }) => ($variant === 'warning' ? colors.warning25 : colors.danger25)};
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    color: ${({ $variant }) => ($variant === 'warning' ? colors.warning100 : colors.danger)};
    font-size: 0.875rem;
    line-height: 1.5;
  `,

  ErrorIcon: styled.span`
    flex-shrink: 0;
  `,

  ThinkingSparkles: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    margin-top: 0.5rem;
    min-height: 1.25rem;

    svg {
      animation: fa-pulse-opacity 1.4s ease-in-out infinite;
    }

    @keyframes fa-pulse-opacity {
      0% {
        opacity: 0.35;
      }
      50% {
        opacity: 0.9;
      }
      100% {
        opacity: 0.35;
      }
    }
  `,

  Skeleton: styled.div`
    width: 100%;
    margin-bottom: 0.75rem;
  `,

  SkeletonHeader: styled.div`
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: ${colors.grey5};
    border-bottom: 2px solid ${colors.grey20};
  `,

  SkeletonRow: styled.div`
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${colors.grey10};

    &:last-child {
      border-bottom: none;
    }
  `,

  SkeletonCell: styled.span`
    height: 0.75rem;
    border-radius: 0.125rem;
    ${({ $onBand }) => barSweep($onBand)}
  `,

  SkeletonChart: styled.div`
    height: 12rem;
    border-radius: 0.25rem;
    ${barSweep(false)}
  `,

  ThinkingText: styled.span`
    font-size: 0.8125rem;
    font-style: italic;
    line-height: 1.25rem;

    background: linear-gradient(
      90deg,
      ${colors.grey40} 0%,
      ${colors.grey40} 40%,
      ${colors.grey75} 50%,
      ${colors.grey40} 60%,
      ${colors.grey40} 100%
    );
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: ${sweep} 2.2s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      background: none;
      -webkit-text-fill-color: ${colors.grey40};
      color: ${colors.grey40};
    }
  `,
};

function Parts({ parts, isLast }) {
  // Map callId → downloadable recordset (from visible data parts and from
  // hidden raw-recordset parts) so the status bar can offer a per-execution CSV.
  const downloads = {};
  for (const p of parts) {
    if (p.callId && p.data && p.fields) {
      downloads[p.callId] = { data: p.data, fields: p.fields, filename: p.filename };
    }
  }

  return parts.map((part, i) => {
    const key = `${part.type}-${i}`;
    // Hidden parts are storage-only (raw recordsets for status-bar download).
    if (part.hidden) return null;
    switch (part.type) {
      case 'tools':
        return <ToolStatusBar key={key} tools={part.tools} downloads={downloads} />;

      case 'text':
        return <Markdown key={key} content={part.content} />;

      case 'chart':
        return (
          <Chart
            key={key}
            chartType={part.chartType}
            data={part.data}
            fields={part.fields}
            dimension={part.dimension}
            metric={part.metric}
            series={part.series}
            colors={part.colors}
            filename={part.filename}
          />
        );

      case 'table':
      case 'schedule':
      case 'pivot':
      case 'component': {
        const tool = part.meta?.contract?.tool;
        const Renderer = resolveRenderer(tool, part.type);
        // 'component' has no generic default — an unregistered tool renders nothing rather than crashing.
        if (!Renderer) return null;
        return (
          <Renderer
            key={key}
            data={part.data}
            fields={part.fields}
            display={part.display}
            meta={part.meta}
            pagination={part.pagination}
            filename={part.filename}
            cellValue={part.cellValue}
            isLast={isLast}
          />
        );
      }

      case 'pending':
        return (
          <S.Skeleton key={key} data-testid="pending-item" data-item-type={part.itemType}>
            {part.itemType === 'chart' ? (
              <S.SkeletonChart />
            ) : (
              <>
                <S.SkeletonHeader>
                  <S.SkeletonCell $onBand />
                  <S.SkeletonCell $onBand />
                  <S.SkeletonCell $onBand />
                  <S.SkeletonCell $onBand />
                </S.SkeletonHeader>
                {[0, 1, 2, 3].map((row) => (
                  <S.SkeletonRow key={row}>
                    <S.SkeletonCell />
                    <S.SkeletonCell />
                    <S.SkeletonCell />
                    <S.SkeletonCell />
                  </S.SkeletonRow>
                ))}
              </>
            )}
          </S.Skeleton>
        );

      case 'error': {
        const variant = part.code === 'AI_BUDGET_LIMIT' ? 'warning' : 'danger';
        const iconColor = variant === 'warning' ? colors.warning100 : colors.danger;
        return (
          <S.ErrorBlock key={key} $variant={variant}>
            <S.ErrorIcon>
              <Icon icon="exclamation-triangle" color={iconColor} />
            </S.ErrorIcon>
            {part.message}
          </S.ErrorBlock>
        );
      }

      default:
        return null;
    }
  });
}

// What the assistant is doing right now, for the line beside the sparkle. A running step wins over
// the reasoning heading — once a tool executes, the step names the actual work.
function currentActivity(parts, thinkingText) {
  const running = (parts.find((p) => p.type === 'tools')?.tools ?? []).filter((t) => t.status === 'running');
  if (running.length === 0) return thinkingText ?? null;

  // A program's step is a container for the queries beneath it; those describe the work better.
  const parentIds = new Set(running.map((t) => t.parentCallId).filter(Boolean));
  const leaves = running.filter((t) => !parentIds.has(t.callId));

  return leaves.length === 1 ? leaves[0].statusText : `Running ${leaves.length} steps`;
}

export default function AssistantMessage({ message, conversationId, isLast, thinking, thinkingText }) {
  const hasContent = message.parts.some(
    (p) => !p.hidden && (p.type === 'text' || p.type === 'table' || p.type === 'chart' || p.type === 'component'),
  );
  const activity = currentActivity(message.parts, thinkingText);

  return (
    <S.Turn $last={isLast}>
      <S.Message>
        <Parts parts={message.parts} isLast={isLast} />

        {thinking && (
          <S.ThinkingSparkles data-testid="thinking-indicator">
            <Icon icon="sparkles" color={colors.primary} />
            {activity && <S.ThinkingText data-testid="thinking-text">{activity}</S.ThinkingText>}
          </S.ThinkingSparkles>
        )}

        {!thinking && hasContent && <ResponseActions message={message} conversationId={conversationId} />}
      </S.Message>
    </S.Turn>
  );
}
